import { getFetch, postFetch, PAY_AT_PICKUP_SKU } from "./Util";
import { shortDisplayTime, anonymizeEmail, anonymizePhone } from "./TypedUtil";
import { getRestaurantSlug } from "./o2";
import { gtin } from "cdigit";
import { getSavedPrinters } from "../../services/printerDb";
import { SavedPrinter } from "../../models/printer";
import { isLabelAutoPrintDisabled } from "../../services/labelAutoPrintDb";

// Magic port of our print agent running on the local machine.
// For now, we only allow displays with locally running print agent to print.
const PRINT_AGENT_URL = "http://localhost:27245";

// List of printer addresses, in fallback order.
async function getOrderPrinters(): Promise<any[]> {
  const allPrinters = await getSavedPrinters();
  const printers = allPrinters
    .filter((p: SavedPrinter) => p.type === "Ticket")
    .map((p: SavedPrinter) => ({
      type: "GENERIC_ESC_POS",
      port: p.network?.port ?? 0,
      ip: p.network?.ip,
      usb: p.usb?.id ?? null,
    }));

  return [
    ...printers,

    // Legacy hard coded values
    {
      type: "GENERIC_ESC_POS",
      port: 9100,
      ip: "192.168.27.211",
    },
    {
      type: "GENERIC_ESC_POS",
      port: 9100,
      ip: "192.168.27.212",
    },
  ];
}

async function getLabelPrinters(): Promise<any[]> {
  const allPrinters = await getSavedPrinters();
  const printers = allPrinters
    .filter((p: SavedPrinter) => p.type === "BrotherQL")
    .map((p: SavedPrinter) => ({
      type: "BROTHER_QL_RASTER",
      port: p.network?.port ?? 0,
      ip: p.network?.ip,
      usb: p.usb?.id ?? null,
    }));

  return [
    ...printers,

    // Legacy hard coded values
    {
      type: "BROTHER_QL_RASTER",
      port: 9100,
      ip: "192.168.27.191",
    },
    {
      type: "BROTHER_QL_RASTER",
      port: 9100,
      ip: "192.168.27.192",
    },
  ];
}

// Builds the print agent address for a single saved printer.
const printerAddressFor = (printer: SavedPrinter) => ({
  type: printer.type === "BrotherQL" ? "BROTHER_QL_RASTER" : "GENERIC_ESC_POS",
  port: printer.network?.port ?? 0,
  ip: printer.network?.ip,
  usb: printer.usb?.id ?? null,
});

// Prints a test page to a specific saved printer.
// Ticket printers print a test receipt; BrotherQL printers print a test label.
export const printTestPage = (
  printer: SavedPrinter,
  successCallback: () => void,
  errCallback: (err: any) => void
): void => {
  const isTicket = printer.type !== "BrotherQL";
  const printerType = isTicket ? "GENERIC_ESC_POS" : "BROTHER_QL_RASTER";

  const printerName = printer.usb
    ? `${printer.usb.manufacturer} ${printer.usb.product}`
    : `${printer.network?.vendor} ${printer.network?.ip}`;

  const printTime = new Date().toLocaleString("en-ZA", {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  let lines: any[];
  if (isTicket) {
    lines = [
      { data: "\n\n\n\n\n\n\n\n\n", align: "CENTER" as const, large_font: true },
      { data: "*** TEST PAGE ***", align: "CENTER" as const, large_font: true },
      { data: "", large_font: true },
      { data: printerName, align: "CENTER" as const, large_font: true },
      { data: printer.type, align: "CENTER" as const },
      { data: "", large_font: true },
      { data: "If you can read this, your printer is set up correctly!", align: "CENTER" as const },
      { data: "", align: "CENTER" as const },
      { data: "Print Time: " + printTime, align: "CENTER" as const },
      { data: "\n\n\n\n\n\n\n\n" },
    ];
  } else {
    lines = [
      { data: "*** TEST LABEL ***" },
      { data: printerName },
      { data: printer.type },
      { data: "Print Time: " + printTime },
    ];
  }

  const request = {
    printer_type: printerType,
    printer_addresses: [printerAddressFor(printer)],
    lines: lines,
  };

  postFetch(
    PRINT_AGENT_URL + "/v2/print_multiple",
    { requests: [request] },
    () => {
      successCallback();
    },
    (err) => {
      errCallback(err);
    },
    true
  );
};

export const scanPrinters = (): void => {
  postFetch(
    PRINT_AGENT_URL + "/v1/scan",
    {},
    (resp, date, etag) => {},
    (err) => {},
    true
  );
};

export const getPrinters = (callback: (printers: any, err: any) => void): void => {
  getFetch(
    `${PRINT_AGENT_URL}/v1/printers`,
    (printers) => {
      callback(printers, null);
    },
    (err) => {
      callback(null, err);
    },
    true
  );
};

const isCloverOrder = (order: any): boolean => {
  return order.id.startsWith("odr_clover");
};

const isDeliveryOrder = (order: any): boolean => {
  if (order.pos_order_type) {
    // TODO(kku): handle other delivery types
    const t = order.pos_order_type.toLowerCase();
    return t === "doordash" || t === "ubereats" || t === "uber eats";
  }
  return false;
};

export const printOnStartOrder = async (
  order: any,
  errCallback: (err: any) => void
): Promise<void> => {
  // Don't auto-print orders from clover - some items might be delayed.
  // Current workflow at restaurant is to use the ticket printed by clover
  // to fulfill an order.
  if (!isCloverOrder(order)) {
    try {
      // Check if label auto-print is disabled for this order
      const labelAutoPrintDisabled = await isLabelAutoPrintDisabled(order.id);

      // Always print the order receipt
      // Conditionally print labels based on toggle state
      const reqs = await Promise.all([
        printOrderRequest(order),
        ...(labelAutoPrintDisabled ? [] : createPrintItemRequests(order, false)),
      ]);

      postFetch(
        PRINT_AGENT_URL + "/v2/print_multiple",
        { requests: reqs },
        () => {},
        (err) => {
          errCallback(err);
        },
        true
      );
    } catch (err) {
      console.error(err);
      errCallback(err);
    }
  }
};

export const printOnOrderDone = (order: any, errCallback: (err: any) => void): void => {
  // Print point QR code for clover orders since items might arrive late,
  // so the total/points is only correct when order is done.
  // Point card for delivery order is placed in bag, so pring separately.
  if (isCloverOrder(order) || isDeliveryOrder(order)) {
    if (order.point_credit) {
      printPointCard(
        order,
        () => {},
        (err) => {
          errCallback(err);
        }
      );
    }
  }
};

export const printPointCard = async (
  order: any,
  successCallback: () => void,
  errCallback: (err: any) => void
): Promise<void> => {
  try {
    const req = await printPointCardRequest(order);
    postFetch(
      PRINT_AGENT_URL + "/v2/print_multiple",
      { requests: [req] },
      (resp, date, etag) => {
        successCallback();
      },
      (err) => {
        errCallback(err);
      },
      true
    );
  } catch (err) {
    console.error(err);
    errCallback(err);
  }
};

export const reprintItemLabel = async (
  order: any,
  item: any,
  successCallback: () => void,
  errCallback: (err: any) => void
): Promise<void> => {
  try {
    const req = await printItemRequest(order, item.attributes, "(reprint)");
    postFetch(
      PRINT_AGENT_URL + "/v2/print_multiple",
      { requests: [req] },
      (resp, date, etag) => {
        successCallback();
      },
      (err) => {
        errCallback(err);
      },
      true
    );
  } catch (err) {
    console.error(err);
    errCallback(err);
  }
};

export const printOrder = async (
  order: any,
  successCallback: () => void,
  errCallback: (err: any) => void
): Promise<void> => {
  try {
    // Print only the order receipt/ticket (not drink labels).
    // Use separate buttons to print drink labels or food labels.
    const reqs = await Promise.all([printOrderRequest(order)]);

    postFetch(
      PRINT_AGENT_URL + "/v2/print_multiple",
      { requests: reqs },
      (resp, date, etag) => {
        successCallback();
      },
      (err) => {
        errCallback(err);
      },
      true
    );
  } catch (err) {
    console.error(err);
    errCallback(err);
  }
};

// Print a label for every item that usually don't print label (e.g. bento)
export const printNonLabelItemInOrder = async (
  order: any,
  successCallback: () => void,
  errCallback: (err: any) => void
): Promise<void> => {
  try {
    const requests = await Promise.all(createPrintItemRequests(order, true));
    postFetch(
      PRINT_AGENT_URL + "/v2/print_multiple",
      { requests },
      (resp, date, etag) => {
        successCallback();
      },
      (err) => {
        errCallback(err);
      },
      true
    );
  } catch (err) {
    console.error(err);
    errCallback(err);
  }
};

// Print a label for every item that usually prints labels (e.g. drinks)
export const printLabelItemInOrder = async (
  order: any,
  successCallback: () => void,
  errCallback: (err: any) => void
): Promise<void> => {
  try {
    const requests = await Promise.all(createPrintItemRequests(order, false));
    postFetch(
      PRINT_AGENT_URL + "/v2/print_multiple",
      { requests },
      (resp, date, etag) => {
        successCallback();
      },
      (err) => {
        errCallback(err);
      },
      true
    );
  } catch (err) {
    console.error(err);
    errCallback(err);
  }
};

export const createPrintItemRequests = (
  order: any,
  invertLabelCondition: boolean
): Promise<any>[] => {
  const items_to_print: any[] = [];
  order.items.forEach((item: any) => {
    const printLabel = invertLabelCondition ? !item.print_label : item.print_label;

    if (item.attributes != null && printLabel) {
      let count = 1;
      if (item.hasOwnProperty("qty")) {
        count = item["qty"];
      }
      for (let i = 0; i < count; i++) {
        items_to_print.push(item);
      }
    }
  });

  return items_to_print.map((item, i) => {
    const suffix = "(" + (i + 1).toString() + "/" + items_to_print.length.toString() + ")";
    return printItemRequest(order, item.attributes, suffix);
  });
};

const formatExpireTime = (pointCredit: any): any[] => {
  if (pointCredit && pointCredit.expire_time) {
    const expireTime = new Date(pointCredit.expire_time).toLocaleDateString("en-ZA", {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return [
      {
        data: "\n",
        large_font: true,
      },
      {
        data: `Points will expire on ${expireTime}`,
        align: "CENTER",
      },
    ];
  }
  return [];
};

export const printPointCardRequest = async (order: any): Promise<any> => {
  let lines: any[] = [];

  lines.push({
    data: order.pos_order_name ? order.pos_order_name : order.pos_order_id,
    align: "CENTER",
    large_font: true,
  });
  lines.push({
    data: order.pos_order_type,
    align: "CENTER",
    large_font: true,
  });
  lines.push({
    data: "",
    large_font: true,
  });
  // TODO(kku): Don't hardcode restaurant name on point card.
  lines.push({
    data: "O2 Valley Loyalty Points",
    align: "CENTER",
    large_font: true,
  });
  lines.push({
    data: "",
    large_font: true,
  });

  if (order.point_credit) {
    lines.push({
      data: `You earned ${order.point_credit.amount} points with this order!`,
      align: "CENTER",
    });

    lines.push({
      data: "",
      align: "CENTER",
    });

    lines.push({
      data: `You can use points to redeem for food and drinks on o2-valley.com`,
      align: "CENTER",
    });

    lines.push({
      data: "\n",
      align: "CENTER",
    });

    // If the customer was not logged in, print QR code for them to claim their
    // points.
    lines.push({
      data: "Scan the QR code to claim your points!",
      align: "CENTER",
    });

    lines.push({
      data: `https://order.o2-valley.com/${await getRestaurantSlug(order.restaurant_id)}/claim/${order.point_credit.id}`,
      align: "CENTER",
      qr_code: true,
    });

    lines = lines.concat(formatExpireTime(order.point_credit));
  }

  lines.push({
    data: "\n",
    large_font: true,
  });

  if (order.point_credit_id) {
    lines.push({
      data: `Credits ID: ${order.point_credit_id}`,
    });
  }

  lines.push({
    data: order.id + "\n",
  });
  lines.push({
    // Use South Africa locale to print 24hr time.
    data:
      "Print Time: " +
      new Date().toLocaleString("en-ZA", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }) +
      "\n",
  });

  // Create enough empty lines for a clean cut.
  lines.push({
    data: "\n\n\n\n\n\n",
  });

  // print.Request https://github.com/TicketRails/print/blob/335bc106bbbd5c5abbfd60a1ac502c5b340c65d0/request.go#L11
  return {
    printer_type: "GENERIC_ESC_POS",
    printer_addresses: await getOrderPrinters(),
    lines: lines,
  };
};

// Returns data for UPC-A barcode.
export function createPayAtPickupBarcodeData(order: any): string | null {
  const total = order.pretax_price ?? 0;
  if (order.paid || total === 0) {
    return null;
  }

  if (total > 99999) {
    // We can only have up to 5 digit price ($999.99) in barcode. We don't print
    // barcode if price is too high, but we'll still show the order is UNPAID on
    // the receipt so clerk can figure out how to charge.
    return null;
  }

  return gtin.generate(`2${PAY_AT_PICKUP_SKU}${total.toString().padStart(5, "0")}`);
}

export const printOrderRequest = async (order: any): Promise<any> => {
  // Group items by attribute id.
  let total_item_count = 0;
  const item_groups: Record<string, any> = {}; // map item attribute id => {count, attributes}
  order.items.forEach((item: any) => {
    if (item.attributes) {
      let new_count = 1;
      if (item.hasOwnProperty("qty")) {
        new_count = item["qty"];
      }
      total_item_count += new_count;

      if (item.attributes.id in item_groups) {
        item_groups[item.attributes.id].count += new_count;
        item_groups[item.attributes.id].redeemed |= item.redeemed;
      } else {
        item_groups[item.attributes.id] = {
          count: new_count,
          attributes: item.attributes,
          item_schema_group: item.item_schema_group,
          print_label: item.print_label,
          redeemed: item.redeemed,
        };
      }
    }
  });

  let lines: any[] = [];
  lines.push({
    // Some spacing from the top of page for clipping to physical ticket rails.
    data: "\n\n\n\n\n\n\n\n\n",
    align: "CENTER",
    large_font: true,
  });

  if (!order.paid) {
    lines.push({
      data: "*** UNPAID ***",
      align: "CENTER",
      large_font: true,
    });
  }

  lines.push({
    data: order.pos_order_name ? order.pos_order_name : order.pos_order_id,
    align: "CENTER",
    large_font: true,
  });
  lines.push({
    data: order.pos_order_type,
    align: "CENTER",
    large_font: true,
  });
  lines.push({
    data: "",
    large_font: true,
  });

  if (order.customer) {
    // Print customer name up top, additional info on the bottom.
    lines.push({
      data: "Customer: " + order.customer.name,
      large_font: true,
    });
  }

  if (order.estimated_pickup_time) {
    try {
      const pickup = new Date(order.estimated_pickup_time);
      lines.push({
        data: "Pickup: " + shortDisplayTime(pickup),
        large_font: true,
      });
    } catch (err) {
      // Give up
      console.log("Failed to parse estimated pickup time", order.estimated_pickup_time, err);
    }
  }

  lines.push({
    data: "\nItem Count: " + total_item_count.toString() + "\n",
    large_font: true,
  });

  lines.push({
    data: "",
    large_font: true,
  });

  lines.push({
    data: order.notes + "\n",
    large_font: true,
  });

  const get_group_name = function (group: any): string {
    return group.attributes.alternate_name
      ? group.attributes.alternate_name
      : group.attributes.name;
  };

  const groups = Object.values(item_groups);
  groups.sort((g1: any, g2: any) => {
    // Put items that are printed on separate labels last.
    if (g1.print_label && !g2.print_label) {
      return 1;
    } else if (!g1.print_label && g2.print_label) {
      return -1;
    }

    // Sort by item schema group sequence first.
    const seq1 = g1.item_schema_group?.seq ?? 0;
    const seq2 = g2.item_schema_group?.seq ?? 0;
    if (seq1 !== seq2) {
      return seq1 - seq2;
    }

    // Then sort by name within each group.
    return get_group_name(g1).localeCompare(get_group_name(g2));
  });

  groups.forEach((group: any) => {
    const redeemed = group.redeemed ? " [R]" : "";

    lines.push({
      data: " 【" + group.count.toString() + "】" + " " + get_group_name(group) + redeemed,
      large_font: true,
    });

    if (group.attributes.modifications) {
      group.attributes.modifications.forEach((mod: string) => {
        lines.push({
          data: mod,
          align: "RIGHT",
          large_font: true,
        });
      });
    }

    if (group.attributes.notes) {
      lines.push({
        data: group.attributes.notes + "\n",
        large_font: true,
      });
    }
  });

  // For delivery orders, print point card separately to place in bag.
  if (order.point_credit && !isDeliveryOrder(order)) {
    lines.push({
      data: "\n",
      large_font: true,
    });

    // If the customer was not logged in, print QR code for them to claim their
    // points.
    if (order.point_credit.claim_customer_id) {
      lines.push({
        data: `${order.point_credit.amount} points have been added to your account!`,
        align: "CENTER",
      });

      lines = lines.concat(formatExpireTime(order.point_credit));
    } else {
      lines.push({
        data: `You earned ${order.point_credit.amount} points with this order!`,
        align: "CENTER",
      });

      lines.push({
        data: "",
        align: "CENTER",
      });

      lines.push({
        data: `You can use points to redeem for food and drinks on o2-valley.com`,
        align: "CENTER",
      });

      lines.push({
        data: "\n",
        align: "CENTER",
      });

      lines.push({
        data: "Scan the QR code to claim your points!",
        align: "CENTER",
      });

      lines.push({
        data: `https://order.o2-valley.com/${await getRestaurantSlug(order.restaurant_id)}/claim/${order.point_credit.id}`,
        align: "CENTER",
        qr_code: true,
      });

      lines = lines.concat(formatExpireTime(order.point_credit));
    }
  }

  lines.push({
    data: "\n",
    large_font: true,
  });

  if (order.customer) {
    lines.push({
      data: "Customer Email: " + anonymizeEmail(order.customer.email ?? ""),
    });
    lines.push({
      data: "Customer Phone: " + anonymizePhone(order.customer.phone ?? ""),
    });
  }

  if (order.point_credit_id && !order.customer_id) {
    lines.push({
      data: `Credits ID: ${order.point_credit_id}`,
    });
  }

  lines.push({
    data: order.id + "\n",
  });
  lines.push({
    // Use South Africa locale to print 24hr time.
    data:
      "Print Time: " +
      new Date().toLocaleString("en-ZA", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }) +
      "\n",
  });

  if (!order.paid) {
    lines.push({
      data: "\n\n*** UNPAID ***",
      align: "CENTER",
      large_font: true,
    });
  }

  // Create enough empty lines for a clean cut.
  lines.push({
    data: "\n\n\n\n\n\n\n\n",
  });

  // print.Request https://github.com/TicketRails/print/blob/335bc106bbbd5c5abbfd60a1ac502c5b340c65d0/request.go#L11
  return {
    printer_type: "GENERIC_ESC_POS",
    printer_addresses: await getOrderPrinters(),
    lines: lines,
  };
};

// Prints an item label
const printItemRequest = async (order: any, item_attrs: any, suffix: string): Promise<any> => {
  const order_name = order.pos_order_name ? order.pos_order_name : order.pos_order_id;
  const lines: any[] = [
    {
      data: order_name + " - " + order.pos_order_type + " " + suffix,
    },
    {
      data: item_attrs.alternate_name ? item_attrs.alternate_name : item_attrs.name,
    },
  ];

  if (item_attrs.modifications) {
    const mods = item_attrs.modifications.join();
    if (mods) {
      lines.push({
        data: mods,
      });
    }
  }

  if (item_attrs.notes) {
    lines.push({
      data: item_attrs.notes,
    });
  }

  // print.Request https://github.com/TicketRails/print/blob/335bc106bbbd5c5abbfd60a1ac502c5b340c65d0/request.go#L11
  return {
    printer_type: "BROTHER_QL_RASTER",
    printer_addresses: await getLabelPrinters(),
    lines: lines,
    // TODO: figure out if rotate actually works. Seems broken for new print
    // agent (digest 082ce579fb2c on dockerhub)
    // "rotate": 180,
  };
};
